// Login + MFA + remember-me + auto-login, ported from design/login.jsx.
import { useEffect, useState, type FormEvent } from "react";
import { APP_INSTALL_URL, isInsideTimboxApp } from "../../shared/nativeApp";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { I } from "../../shared/Icon";
import { authApi } from "../../shared/api/auth";
import { useAuth } from "../../shared/stores/auth";
import { toast } from "../../shared/stores/toasts";

const REMEMBER_KEY = "timple-remember-username";
const AUTO_LOGIN_KEY = "timple-auto-login";

export default function LoginPage() {
  const nav = useNavigate();
  // 로그인이 필요해 튕겨 온 곳(예: 다른 앱에서 'TIM_BOX 로 열기' → 통합 문서 작성)으로 돌려보낸다
  const from = (useLocation().state as { from?: string } | null)?.from;
  const after = from && from.startsWith("/") && !from.startsWith("/login") ? from : "/dashboard";
  const { setTokens, setUser, setManifest, isAuthenticated } = useAuth();

  const [step, setStep] = useState<"login" | "mfa">("login");
  const [username, setUsername] = useState(() => localStorage.getItem(REMEMBER_KEY) ?? "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [autoLogin, setAutoLogin] = useState(() => localStorage.getItem(AUTO_LOGIN_KEY) === "1");
  const [mfaSession, setMfaSession] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Auto-login if already authenticated
  useEffect(() => {
    if (isAuthenticated()) nav(after);
  }, [isAuthenticated, nav, after]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const res = await authApi.login(username, password);
      if (res.mfa_required && res.mfa_session) {
        setMfaSession(res.mfa_session);
        setStep("mfa");
        toast.info("MFA 코드를 입력하세요", "Google Authenticator");
      } else if (res.access_token && res.refresh_token) {
        finishLogin(res.access_token, res.refresh_token);
      }
    } catch (e: any) {
      setErr(e?.response?.data?.error?.message ?? "로그인에 실패했습니다");
    } finally {
      setSubmitting(false);
    }
  };

  const submitMfa = async (e: FormEvent) => {
    e.preventDefault();
    if (!mfaSession) return;
    setSubmitting(true);
    setErr(null);
    try {
      const res = await authApi.verifyMfa(mfaSession, code);
      if (res.access_token && res.refresh_token) finishLogin(res.access_token, res.refresh_token);
    } catch (e: any) {
      setErr(e?.response?.data?.error?.message ?? "MFA 코드가 올바르지 않습니다");
    } finally {
      setSubmitting(false);
    }
  };

  const finishLogin = async (access: string, refresh: string) => {
    setTokens(access, refresh);
    if (remember) localStorage.setItem(REMEMBER_KEY, username); else localStorage.removeItem(REMEMBER_KEY);
    if (autoLogin) localStorage.setItem(AUTO_LOGIN_KEY, "1"); else localStorage.removeItem(AUTO_LOGIN_KEY);
    try {
      const [me, mf] = await Promise.all([authApi.me(), authApi.manifest()]);
      setUser(me);
      setManifest(mf);
    } catch { /* ignored — interceptor will redirect if 401 */ }
    toast.success("환영합니다!", username);
    nav(after);
  };

  return (
    <div className="timple-root">
      <div className="t-login" style={{ height: "100%" }}>
        <div className="t-login-brand">
          <div>
            <img src="/timple-logo.jpg" alt="TIMPLE" style={{ height: 32, background: "white", padding: 6, borderRadius: 6 }}/>
          </div>
          <div>
            {after.startsWith("/teamdocs") ? (
              // '팀_문서' 앱에서 들어온 로그인 — 문서 앱에 맞는 소개로
              <>
                <h1>팀_문서</h1>
                <p>한글 · 워드 · 엑셀 · 파워포인트 · PDF · 포토샵 · 일러스트. 설치 없이 보고, 고치고, 새로 만드세요. TIM_BOX 계정으로 로그인합니다.</p>
              </>
            ) : (
              <>
                <h1>전사 업무를 하나의 캔버스에</h1>
                <p>결재 · 메일 · 메신저 · 캘린더 · 프로젝트 · 인사 · 재무. 흩어진 도구를 통합한 TIMPLE에서 더 빠르게 일하세요.</p>
              </>
            )}
          </div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>© Tim-box · 안전한 사내 VPN을 통해 접속하세요.</div>
        </div>

        <div className="t-login-pane">
          <form className="t-login-form" onSubmit={step === "login" ? submit : submitMfa}>
            {step === "login" ? (
              <>
                <h2>로그인</h2>
                <div className="subtitle">사내 계정으로 로그인하세요.</div>

                <div style={{ marginBottom: 12 }}>
                  <label className="t-label">아이디</label>
                  <input className="t-input" type="text" autoComplete="username" value={username}
                         onChange={(e) => setUsername(e.target.value)} required autoFocus />
                </div>

                <div style={{ marginBottom: 12, position: "relative" }}>
                  <label className="t-label">비밀번호</label>
                  <input className="t-input" type={showPw ? "text" : "password"} autoComplete="current-password" value={password}
                         onChange={(e) => setPassword(e.target.value)} required style={{ paddingRight: 40 }}/>
                  <button type="button" onClick={() => setShowPw((s) => !s)}
                          style={{ position: "absolute", right: 10, top: 28, background: "none", border: "none", color: "var(--text-3)" }}>
                    {showPw ? <I.EyeOff size={16}/> : <I.Eye size={16}/>}
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, fontSize: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                    아이디 기억
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={autoLogin} onChange={(e) => { setAutoLogin(e.target.checked); if (e.target.checked) setRemember(true); }} />
                    자동 로그인
                  </label>
                  <Link to="/forgot" style={{ color: "var(--p-600)", textDecoration: "none" }}>비밀번호 찾기</Link>
                </div>

                {err && <div className="t-pill danger" style={{ width: "100%", justifyContent: "center", padding: 8, marginBottom: 12 }}>{err}</div>}

                <button className="t-btn t-btn-primary" type="submit" style={{ width: "100%", height: 42 }} disabled={submitting}>
                  {submitting ? "로그인 중…" : "로그인"}
                </button>

                <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "var(--text-3)" }}>
                  아직 계정이 없으신가요? <Link to="/signup" style={{ color: "var(--p-600)", textDecoration: "none", fontWeight: 600 }}>회원가입</Link>
                </div>

                {/* 설치 앱을 받는 곳. 앱 안에서 열린 로그인 화면이면 보여 줄 이유가 없다. */}
                {!isInsideTimboxApp() && (
                  <div style={{ textAlign: "center", marginTop: 8, fontSize: 13, color: "var(--text-3)" }}>
                    윈도우·안드로이드 앱으로 쓰려면{" "}
                    <a href={APP_INSTALL_URL} target="_blank" rel="noopener noreferrer"
                       style={{ color: "var(--p-600)", textDecoration: "none", fontWeight: 600 }}>
                      앱 받기
                    </a>
                  </div>
                )}

                <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 8 }}>
                  <button type="button" className="t-btn t-btn-outline" style={{ width: "100%" }}>Google로 계속하기</button>
                  <button type="button" className="t-btn t-btn-outline" style={{ width: "100%" }}>Microsoft로 계속하기</button>
                </div>
              </>
            ) : (
              <>
                <h2>2차 인증</h2>
                <div className="subtitle">Authenticator 앱의 6자리 코드를 입력하세요.</div>

                <div style={{ marginBottom: 12 }}>
                  <input className="t-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                         placeholder="000000" autoFocus style={{ fontSize: 24, letterSpacing: 8, textAlign: "center" }} />
                </div>

                {err && <div className="t-pill danger" style={{ width: "100%", justifyContent: "center", padding: 8, marginBottom: 12 }}>{err}</div>}

                <button className="t-btn t-btn-primary" type="submit" style={{ width: "100%", height: 42 }} disabled={submitting || code.length < 6}>
                  {submitting ? "검증 중…" : "확인"}
                </button>

                <button type="button" onClick={() => { setStep("login"); setCode(""); }} className="t-btn" style={{ width: "100%", marginTop: 8 }}>
                  뒤로
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
